import Utils from '../../../utils/utils';
import { stripDualTravelMileageJsonFields } from './conditional-fields.js';

/** Normalize Mongo-style id from API JSON (string or `{ $oid }`). */
export function mongoExtendedJsonId(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  }
  if (typeof value === 'object' && value.$oid) return String(value.$oid);
  const asString = String(value);
  return asString === '[object Object]' ? null : asString;
}

/** Normalize Mongo-style `_id` from API JSON (string or `{ $oid }`). */
export function imputationDocumentId(doc) {
  if (!doc || doc._id == null) return '';
  return mongoExtendedJsonId(doc._id) || '';
}

/** Account or expense GET payload `user` blob → principal id for imputation scoping (XPEN-2455). */
export function resolvePrincipalUserIdFromPayloadUser(user) {
  if (!user) return null;
  return mongoExtendedJsonId(user.id_user) || mongoExtendedJsonId(user._id);
}

/** CONTRACT-01 §6 — flat keys (PascalCase, aligned with MatterId / Category). */
export const EXPENSE_JSON_IMPUTATION_LEVEL1_ID = 'ImputationLevel1Id';
export const EXPENSE_JSON_IMPUTATION_LEAF_ID = 'ImputationLeafId';

function normalizeImputationId(value) {
  if (value == null || value === '') return null;
  return String(value);
}

function trimImputationIdRaw(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t === '' ? null : t;
  }
  return String(raw);
}

/**
 * Resolve imputation row for expense account editor / display (FE-TX-05, XPEN-2420).
 * Aligns with API `find_imputation_for_expense_json`: valid leaf id first, then Category
 * vs `category` / `maps_from_verify_category`. If there is no leaf id but
 * `ImputationLevel1Id` is set, resolves by that id (UI precedence when review only selected level 1).
 */
export function findImputationRowForExpenseEditor(imputations, json = {}) {
  if (!Array.isArray(imputations) || !json || typeof json !== 'object') return undefined;

  const leafId = trimImputationIdRaw(json[EXPENSE_JSON_IMPUTATION_LEAF_ID]);
  if (leafId) {
    const byLeaf = imputations.find((row) => imputationDocumentId(row) === leafId);
    if (byLeaf) return byLeaf;
  }

  const l1Id = trimImputationIdRaw(json[EXPENSE_JSON_IMPUTATION_LEVEL1_ID]);
  if (l1Id && !leafId) {
    const byL1 = imputations.find((row) => imputationDocumentId(row) === l1Id);
    if (byL1) return byL1;
  }

  const category = json.Category;
  if (category == null || category === '') return undefined;

  return imputations.find(
    (row) => row.category === category || row.maps_from_verify_category === category
  );
}

/**
 * Tax code from imputation rows when the user picks category / subcategory.
 * Leaf wins when both rows define tax_code; otherwise use level-1. Null when
 * neither row supplies a code (caller keeps the current tax code).
 */
export function resolveImputationTaxCodeFromRows({ level1Row, leafRow } = {}) {
  const leafCode = leafRow?.tax_code;
  if (leafCode != null && String(leafCode).trim() !== '') {
    return String(leafCode).trim();
  }
  const level1Code = level1Row?.tax_code;
  if (level1Code != null && String(level1Code).trim() !== '') {
    return String(level1Code).trim();
  }
  return null;
}

/**
 * Resolve a coding field from imputation rows: leaf value first, then level-1.
 * Used when category/subcategory selection drives line-item GL / Cost Type / Office.
 */
function resolveImputationCodingField(field, leafRow, level1Row) {
  const fromLeaf = leafRow?.[field];
  if (fromLeaf != null && String(fromLeaf).trim() !== '') {
    return String(fromLeaf).trim();
  }
  const fromL1 = level1Row?.[field];
  if (fromL1 != null && String(fromL1).trim() !== '') {
    return String(fromL1).trim();
  }
  return '';
}

/** Leaf cost_type wins, then level-1. Empty string when neither row has a code. */
export function resolveImputationCostTypeFromRows({ level1Row, leafRow } = {}) {
  return resolveImputationCodingField('cost_type', leafRow, level1Row);
}

/**
 * XPEN-2939: resolved Cost Type / GL Natural / GL Office from imputation rows
 * (leaf wins, then level-1). Empty strings when a field is unset on both rows.
 */
export function resolveImputationCodingFromRows({ level1Row, leafRow } = {}) {
  return {
    cost_type: resolveImputationCodingField('cost_type', leafRow, level1Row),
    gl_natural: resolveImputationCodingField('gl_natural', leafRow, level1Row),
    gl_office: resolveImputationCodingField('gl_office', leafRow, level1Row),
  };
}

/** Set header and line-item TaxCode on an expense json object. */
export function applyTaxCodeToExpenseJson(json = {}, taxCode) {
  if (!taxCode || !json || typeof json !== 'object') {
    return json;
  }
  return {
    ...json,
    TaxCode: taxCode,
    ...(json.ListItem && {
      ListItem: json.ListItem.map((item) => ({ ...item, TaxCode: taxCode })),
    }),
  };
}

/**
 * Read tree coding ids from persisted expense.json (GET).
 */
export function readImputationIdsFromExpenseJson(json = {}) {
  return {
    imputationLevel1Id: json[EXPENSE_JSON_IMPUTATION_LEVEL1_ID] ?? null,
    imputationLeafId: json[EXPENSE_JSON_IMPUTATION_LEAF_ID] ?? null,
  };
}

/**
 * Values to merge into PUT json for imputation ids. When a field_* flag is off,
 * preserves the existing json value so saves do not strip server-owned data.
 */
export function resolveImputationIdsForPayload(cleanedExpense, json, data) {
  const level1FromForm = Utils.hasExpenseField(data, 'field_imputation_level1', false);

  const ImputationLevel1Id = level1FromForm
    ? normalizeImputationId(cleanedExpense.imputationLevel1Id)
    : json[EXPENSE_JSON_IMPUTATION_LEVEL1_ID] ?? null;

  const ImputationLeafId = level1FromForm
    ? normalizeImputationId(cleanedExpense.imputationLeafId)
    : json[EXPENSE_JSON_IMPUTATION_LEAF_ID] ?? null;

  return { ImputationLevel1Id, ImputationLeafId };
}

/**
 * Imputation tree is primary coding: never overwrite AI `Category` from legacy picker state.
 */
export function usesImputationTreeCoding(data) {
  return Utils.hasExpenseField(data, 'field_imputation_level1', false);
}

/**
 * Preserve header billable flag from GET json (FE-TX-03).
 * Returns null when absent so callers on the preserve path send null to the API
 * rather than forcing true onto clients that never set IsBillable.
 * UI callers that need a boolean for the toggle should use `?? true`.
 */
export function resolveHeaderIsBillable(json) {
  if (json == null) return null;
  const v = json.IsBillable;
  if (v === undefined || v === null) return null;
  return Boolean(v);
}

/**
 * Per line item: keep existing IsBillable when set; else inherit header (FE-TX-03).
 */
export function resolveListItemIsBillable(item, headerIsBillable) {
  if (item == null) return headerIsBillable;
  const v = item.IsBillable;
  if (v === undefined || v === null) return headerIsBillable;
  return Boolean(v);
}

/**
 * Resolve separate L1 (category) and L2 (leaf) imputation rows from expense.json
 * for read-only display in the invoice editor (XPEN-2477).
 * Unlike findImputationRowForExpenseEditor, always returns both rows independently
 * so callers can show a category field and an optional subcategory field side by side.
 */
export function resolveDisplayImputationRows(imputations, json = {}) {
  if (!Array.isArray(imputations)) return { l1Row: undefined, l2Row: undefined };
  const l1Id = trimImputationIdRaw(json[EXPENSE_JSON_IMPUTATION_LEVEL1_ID]);
  const l2Id = trimImputationIdRaw(json[EXPENSE_JSON_IMPUTATION_LEAF_ID]);
  const l1Row = l1Id ? imputations.find((r) => imputationDocumentId(r) === l1Id) : undefined;
  const l2Row = l2Id ? imputations.find((r) => imputationDocumentId(r) === l2Id) : undefined;
  return { l1Row, l2Row };
}

/**
 * AI / Verify Category: always keep persisted `json.Category`.
 * User-selected Category now lives in `ImputationLevel1Id` (and optional `ImputationLeafId`).
 */
export function resolveCategoryForSubmit(json = {}) {
  return json.Category;
}

/**
 * Supplier/Event is optional. When the review field is hidden, keep the
 * persisted value so a hidden admin flag does not clear linked invoice data.
 */
export function resolveSupplierEventForPayload(expenseInfo, json = {}, data) {
  if (Utils.hasExpenseField(data, 'field_supplier_event')) {
    return { SupplierEvent: expenseInfo?.supplier_event || '' };
  }
  if (Object.prototype.hasOwnProperty.call(json, 'SupplierEvent')) {
    return { SupplierEvent: json.SupplierEvent };
  }
  return {};
}

/**
 * Review form amount (Receipt Total / total du reçu). Load from persisted UnadjustedTotal only.
 * On PUT ?view=review, the same value is sent as json.UnadjustedTotal; xpens-api derives
 * ttc_before_tip = UnadjustedTotal - Tip (XPEN-2634 supersedes XPEN-2581).
 */
export function resolveReviewAmountFromExpenseJson(json = {}) {
  const raw = json.UnadjustedTotal;
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Read MatterSplits from persisted expense.json (GET).
 * Returns the array when present, null otherwise.
 */
export function readMatterSplitsFromExpenseJson(json) {
  if (!json || typeof json !== 'object') return null;
  return json.MatterSplits ?? null;
}

/**
 * XPEN-2773 / XPEN-2809: line → header (legacy) → absent.
 * Key presence on the line wins even when the value is empty.
 */
export function resolveExpenseType(json = {}, item = null) {
  if (item != null && Object.prototype.hasOwnProperty.call(item, 'ExpenseType')) {
    return String(item.ExpenseType ?? '').trim().toLowerCase();
  }
  return String(json?.ExpenseType ?? '').trim().toLowerCase();
}

/**
 * XPEN-2773: billable OR client-type expenses use CostType; internal non-billable use GLNatural.
 * XPEN-2809: ExpenseType resolves line-first, then header for legacy shapes.
 */
export function expenseUsesDisbursementCode(json = {}, item = null) {
  const source = item ?? json;
  const isBillable = source?.IsBillable;
  if ([1, true].includes(isBillable)) {
    return true;
  }
  return resolveExpenseType(json, item) === 'client';
}

/**
 * Resolve level-1 and leaf imputation rows from expense.json for editor recoding.
 * Uses stored ids first; falls back to findImputationRowForExpenseEditor (Category match).
 */
export function resolveImputationRowsForExpenseEditor(imputations, json = {}) {
  const { l1Row, l2Row } = resolveDisplayImputationRows(imputations, json);
  if (l1Row || l2Row) {
    return { level1Row: l1Row, leafRow: l2Row };
  }
  const match = findImputationRowForExpenseEditor(imputations, json);
  if (!match) {
    return { level1Row: undefined, leafRow: undefined };
  }
  if (match.parent_id != null && match.parent_id !== '') {
    const parentId = String(match.parent_id);
    const level1Row = imputations.find((row) => imputationDocumentId(row) === parentId);
    return { level1Row, leafRow: match };
  }
  return { level1Row: match, leafRow: undefined };
}

/**
 * Apply imputation GL / Cost Type / Office coding to one expense line item.
 * Leaf row wins when present; otherwise level-1. By default empty resolved values clear
 * the routed fields (including GL Office) so stale subcategory codes are not kept after a category change.
 * When preserveExistingWhenEmpty is true, keeps the line item value if imputation rows
 * supply no coding (expense-type / mileage recode paths).
 */
export function applyImputationCodingToListItem(
  item,
  json = {},
  { level1Row, leafRow, preserveExistingWhenEmpty = false } = {}
) {
  if (!item || typeof item !== 'object') {
    return item;
  }
  const nextItem = { ...item };
  const costType = resolveImputationCodingField('cost_type', leafRow, level1Row);
  const glNatural = resolveImputationCodingField('gl_natural', leafRow, level1Row);
  const glOffice = resolveImputationCodingField('gl_office', leafRow, level1Row);

  if (expenseUsesDisbursementCode(json, nextItem)) {
    nextItem.CostType = costType !== ''
      ? costType
      : (preserveExistingWhenEmpty ? (item.CostType ?? '') : '');
    nextItem.GLNatural = '';
  } else {
    nextItem.GLNatural = glNatural !== ''
      ? glNatural
      : (preserveExistingWhenEmpty ? (item.GLNatural ?? '') : '');
    nextItem.CostType = '';
  }
  if (glOffice !== '') {
    nextItem.GLOffice = glOffice;
    nextItem.Office = glOffice;
  } else if (!preserveExistingWhenEmpty) {
    nextItem.GLOffice = '';
    nextItem.Office = '';
  }
  return nextItem;
}

export const formatMoneyString = (value, fallback = '') => {
  const parsedValue = Number.parseFloat(value);
  if (Number.isNaN(parsedValue)) {
    return fallback;
  }
  return parsedValue.toFixed(2);
};

/**
 * Review form model (the shape ExpenseComponent edits) from a stored expense json.
 * Shared by the review screen and the invoice editor's add-expense modal (XPEN-3551).
 */
export function expenseJsonToReviewModel(json = {}, defaultTaxCode) {
  const imputationIds = readImputationIdsFromExpenseJson(json);
  const matterSplitsFromJson = readMatterSplitsFromExpenseJson(json);
  return {
    matter: json.MatterId || '',
    date: json.InvoiceDate || new Date().toISOString().slice(0, 10),
    category: json.Category || '',
    activity: json.Activity || '',
    costType: json.ReviewCostType || json.CostType || '',
    expenseType: json.ExpenseType || '',
    isBillable: resolveHeaderIsBillable(json) ?? true,
    ...imputationIds,
    matterSplits: matterSplitsFromJson
      ? matterSplitsFromJson.map((s) => ({ matter: s.MatterId || '', percentage: s.Percentage || 0 }))
      : null,
    info: {
      narrative: json.Narrative || '',
      supplier_event: json.SupplierEvent || '',
      location: json.Location || '',
      amount: formatMoneyString(resolveReviewAmountFromExpenseJson(json), '0.00'),
      numberOfGuests: json.NumberOfGuests || 0,
      taxCode: json.TaxCode || defaultTaxCode,
      tip: json.Tip ? formatMoneyString(json.Tip, null) : null,
      distance: json.Distance ? Number(parseFloat(json.Distance).toFixed(2)) : 0,
      conversionTrace: json.OriginalCurrency ? {
        originalCurrency: json.OriginalCurrency,
        originalAmount: json.OriginalAmounts?.Total,
        rate: json.ConversionRate,
        rateDate: json.ConversionRateDate,
        rateSource: json.ConversionRateSource,
      } : null,
    },
  };
}

/** Invoice number and date shown by MainInfo, from a stored expense. */
export function reviewMainInfoFromExpenseData(expenseData) {
  const json = expenseData?.json || {};
  const date = json.InvoiceDate
    ? new Date(json.InvoiceDate).toISOString().slice(0, 10)
    : (expenseData?.created_at?.$date
        ? new Date(expenseData.created_at.$date).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10));
  return { invoiceNumber: json.InvoiceNo || '', date };
}

/**
 * Builds the full PUT payload for the expense review save (CONTRACT-01 §8).
 * `cleanedExpense` must already be run through cleanConditionalFields before calling this.
 * @param {object} [imputationRows] - Resolved `{ level1Row, leafRow }` from the review form catalog (XPEN-2939).
 */
export function buildExpensePayload(cleanedExpense, expenseData, mainInfo, data, imputationRows = null) {
  const json = stripDualTravelMileageJsonFields(
    { ...expenseData.json },
    data,
    cleanedExpense.costType,
    cleanedExpense.category
  );
  const expenseInfo = cleanedExpense.info || {};
  const ownsSupplierEvent = Utils.hasExpenseField(data, 'field_supplier_event');
  const supplierEventPayload = resolveSupplierEventForPayload(expenseInfo, json, data);

  const hasSplits = Array.isArray(cleanedExpense.matterSplits) && cleanedExpense.matterSplits.length > 0;

  const matterNumber = Utils.hasExpenseField(data, 'field_matter')
    ? (hasSplits
        ? cleanedExpense.matterSplits[0]?.matter?.value?.number
        : cleanedExpense.matter?.value?.number)
    : json.MatterId;

  // Object shape (react-select) or string from XPEN-2939 imputation patch before async resolve.
  // Prefer '' over null/undefined so JSON.stringify keeps CostType and clears stale Travel codes.
  const costTypeFromFormRaw = typeof cleanedExpense.costType === 'string'
    ? cleanedExpense.costType
    : cleanedExpense.costType?.value?.cost_type;
  const costTypeFromForm = costTypeFromFormRaw == null ? undefined : String(costTypeFromFormRaw);
  const imputationCoding = cleanedExpense.imputationCoding && typeof cleanedExpense.imputationCoding === 'object'
    ? cleanedExpense.imputationCoding
    : null;

  const category = resolveCategoryForSubmit(json);

  const { ImputationLevel1Id, ImputationLeafId } = resolveImputationIdsForPayload(
    cleanedExpense,
    json,
    data
  );

  const imputationFieldEnabled = Utils.hasExpenseField(data, 'field_imputation_level1', false);
  let applyLevel1Row = null;
  let applyLeafRow = null;
  if (imputationFieldEnabled && ImputationLevel1Id) {
    if (imputationRows?.level1Row) {
      applyLevel1Row = imputationRows.level1Row;
      applyLeafRow = imputationRows.leafRow ?? null;
    } else if (imputationCoding) {
      applyLevel1Row = imputationCoding;
      applyLeafRow = null;
    }
  }
  const hasCodingToApply = applyLevel1Row != null;

  let level1RowForApply = applyLevel1Row;
  let leafRowForApply = applyLeafRow;
  if (hasCodingToApply && Utils.hasExpenseField(data, 'field_cost_type') && costTypeFromForm !== undefined) {
    const merged = resolveImputationCodingFromRows({ level1Row: applyLevel1Row, leafRow: applyLeafRow });
    level1RowForApply = { ...merged, cost_type: costTypeFromForm };
    leafRowForApply = null;
  }

  const costType = Utils.hasExpenseField(data, 'field_cost_type')
    ? (costTypeFromForm ?? '')
    : (hasCodingToApply
        ? (resolveImputationCodingFromRows({ level1Row: level1RowForApply, leafRow: leafRowForApply }).cost_type || '')
        : (json.ReviewCostType || json.CostType));

  let activity = json.Activity;
  if (Utils.hasExpenseField(data, 'field_activity')) {
    const act = cleanedExpense.activity;
    activity = act?.value ?? act?.code ?? act ?? '';
  }

  const taxCode = Utils.hasExpenseField(data, 'field_tax_code')
    ? expenseInfo.taxCode
    : json.TaxCode;

  const narrativeForDescription = Utils.hasExpenseField(data, 'field_narrative')
    ? expenseInfo.narrative
    : (json.Narrative || json.Description || '');

  const tipSource = Utils.hasExpenseField(data, 'field_tips') ? expenseInfo.tip : json.Tip;
  let tipNumber = null;
  if (tipSource != null && tipSource !== '') {
    const n = parseFloat(tipSource);
    tipNumber = Number.isFinite(n) ? n : null;
  }

  const descriptionParts = [
    ownsSupplierEvent ? expenseInfo.supplier_event : json.SupplierEvent,
    expenseInfo.location,
    expenseInfo.distance ? `${expenseInfo.distance} km` : null,
    expenseInfo.numberOfGuests,
    narrativeForDescription,
  ].filter(Boolean);

  const headerIsBillable = Utils.hasExpenseField(data, 'field_billable', false)
    ? Boolean(cleanedExpense.isBillable)
    : resolveHeaderIsBillable(json);

  const resolvedExpenseType = Utils.hasExpenseField(data, 'field_expense_type', false)
    ? (cleanedExpense.expenseType || '')
    : json.ExpenseType;

  const receiptTotal = parseFloat(expenseInfo.amount) || 0;

  const routingJson = { ...json, ExpenseType: resolvedExpenseType, IsBillable: headerIsBillable };

  const listItems = json.ListItem?.map(item => {
    const lineIsBillable = Utils.hasExpenseField(data, 'field_billable', false)
      ? Boolean(cleanedExpense.isBillable)
      : resolveListItemIsBillable(item, headerIsBillable);
    const lineRoutingJson = { ...routingJson };
    const lineBase = {
      ...item,
      MatterId: matterNumber,
      Activity: activity,
      ImputationLevel1Id,
      ImputationLeafId,
      Description: descriptionParts.join(' - ') || item.Description,
      Total: 0,
      TaxCode: taxCode,
      WorkDate: cleanedExpense.date || item.WorkDate || null,
      IsBillable: lineIsBillable,
    };
    if (hasCodingToApply) {
      return applyImputationCodingToListItem(lineBase, lineRoutingJson, {
        level1Row: level1RowForApply,
        leafRow: leafRowForApply,
        preserveExistingWhenEmpty: false,
      });
    }
    const useDisbursement = expenseUsesDisbursementCode(
      lineRoutingJson,
      { ...item, IsBillable: lineIsBillable }
    );
    return {
      ...lineBase,
      CostType: costType,
      ...(useDisbursement ? { GLNatural: '' } : {}),
    };
  }) || [];

  let headerCostType = costType;
  let headerGlNatural = json.GLNatural;
  let headerGlOffice = json.GLOffice ?? json.Office;
  if (hasCodingToApply) {
    const headerCoding = applyImputationCodingToListItem(
      { IsBillable: headerIsBillable },
      routingJson,
      { level1Row: level1RowForApply, leafRow: leafRowForApply, preserveExistingWhenEmpty: false }
    );
    headerCostType = headerCoding.CostType ?? '';
    headerGlNatural = headerCoding.GLNatural ?? '';
    headerGlOffice = headerCoding.GLOffice ?? '';
  }

  const matterSplitsPayload = hasSplits
    ? { MatterSplits: cleanedExpense.matterSplits.map((s) => ({ MatterId: s.matter?.value?.number, Percentage: s.percentage })) }
    : {};

  return {
    ...expenseData,
    json: {
      ...json,
      MatterId: matterNumber,
      ...matterSplitsPayload,
      InvoiceNo: Utils.shouldShowExpenseReviewInvoiceNumber(data)
        ? mainInfo.invoiceNumber
        : json.InvoiceNo,
      InvoiceDate: mainInfo.date,
      Activity: activity,
      CostType: headerCostType,
      ReviewCostType: headerCostType,
      ...(hasCodingToApply
        ? {
            GLNatural: headerGlNatural,
            ...(headerGlOffice !== '' && headerGlOffice != null
              ? { GLOffice: headerGlOffice, Office: headerGlOffice }
              : { GLOffice: '', Office: '' }),
          }
        : {}),
      ExpenseType: Utils.hasExpenseField(data, 'field_expense_type', false)
        ? (cleanedExpense.expenseType || '')
        : json.ExpenseType,
      Category: category,
      CategoryId: json.CategoryId,
      ImputationLevel1Id,
      ImputationLeafId,
      NumberOfGuests: expenseInfo.numberOfGuests,
      Distance: expenseInfo.distance ? parseFloat(expenseInfo.distance) : null,
      UnadjustedTotal: receiptTotal,
      TaxCode: taxCode,
      Tip: tipNumber,
      Location: expenseInfo.location,
      ...supplierEventPayload,
      Narrative: narrativeForDescription || expenseInfo.narrative,
      ListItem: listItems,
      IsBillable: headerIsBillable,
      Reviewed: true,
    },
  };
}
